import React, { useState, useEffect } from 'react';
import { entries as entriesApi, customers as customersApi, customItems } from '../services/api';
import { todayLocalISO, fmtDate } from '../utils/localDate';

const Entries = () => {
  const [windowWidth, setWindowWidth] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;

  // ── Tab state ──────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('time'); // 'time' | 'custom'

  // ── Time Entries state ────────────────────────────────────────────────────
  const [entries, setEntries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [filters, setFilters] = useState({
    customerId: '',
    status: '',
    startDate: '',
    endDate: ''
  });
  const [formData, setFormData] = useState({
    customerId: '',
    date: todayLocalISO(),
    hours: '',
    minutes: '',
    category: 'level1',
    description: '',
    materials: [],
    discount: 0,
    discountType: 'percent',
    taxRate: 0
  });
  const [newMaterial, setNewMaterial] = useState({ name: '', quantity: 1, unitPrice: 0, salesPrice: 0 });

  // ── Custom Items state ────────────────────────────────────────────────────
  const [customItemsList, setCustomItemsList] = useState([]);
  const [customItemsLoading, setCustomItemsLoading] = useState(false);
  const [showCustomItemModal, setShowCustomItemModal] = useState(false);
  const [editingCustomItem, setEditingCustomItem] = useState(null);
  const [customItemFilters, setCustomItemFilters] = useState({
    customerId: '',
    status: '',
    startDate: '',
    endDate: ''
  });
  const [customItemForm, setCustomItemForm] = useState({
    customerId: '',
    date: todayLocalISO(),
    name: '',
    description: '',
    quantity: 1,
    unitPrice: ''
  });

  // ── Recurring services ────────────────────────────────────────────────────
  const [recurringServices] = useState([
    { id: 'monthly_maint', name: 'Monthly Maintenance', hours: 2, description: 'Monthly maintenance visit' },
    { id: 'quarterly_backup', name: 'Quarterly Backup Check', hours: 1, description: 'Quarterly backup verification' },
    { id: 'annual_review', name: 'Annual Review', hours: 4, description: 'Annual system review and optimization' },
    { id: 'emergency_response', name: 'Emergency Response', hours: 1, description: 'Emergency on-site response' },
    { id: 'remote_support', name: 'Remote Support Session', hours: 0.5, description: 'Remote technical support' },
    { id: 'onsite_visit', name: 'Standard On-site Visit', hours: 1, description: 'Standard on-site service call' }
  ]);
  const [selectedService, setSelectedService] = useState('');

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    if (activeTab === 'time') {
      loadEntries();
    } else {
      loadCustomItems();
    }
  }, [filters, customItemFilters, activeTab]);

  // ── Load functions ────────────────────────────────────────────────────────
  const loadCustomers = async () => {
    try {
      const response = await customersApi.list({ isActive: true });
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to load customers:', error);
    }
  };

  const loadEntries = async () => {
    try {
      setLoading(true);
      const response = await entriesApi.list(filters);
      setEntries(response.data.entries || []);
    } catch (error) {
      console.error('Failed to load entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomItems = async () => {
    try {
      setCustomItemsLoading(true);
      const response = await customItems.list(customItemFilters);
      setCustomItemsList(response.data.items || []);
    } catch (error) {
      console.error('Failed to load custom items:', error);
    } finally {
      setCustomItemsLoading(false);
    }
  };

  // ── Time Entry actions ─────────────────────────────────────────────────────
  const updateStatus = async (id, status) => {
    try {
      await entriesApi.update(id, { status });
      // Update local state directly instead of reloading from server
      // This prevents the page from scrolling back to the top
      setEntries(prev => prev.map(e => e.id === id ? { ...e, status } : e));
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status');
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Are you sure you want to DELETE this time entry?')) return;
    if (!window.confirm('PLEASE CONFIRM: This will permanently delete the time entry. This action cannot be undone.')) return;
    try {
      await entriesApi.delete(id);
      loadEntries();
    } catch (error) {
      console.error('Failed to delete entry:', error);
      alert('Failed to delete entry');
    }
  };

  const editEntry = (entry) => {
    setEditingEntry(entry);
    setFormData({
      customerId: entry.customerId || '',
      date: entry.date || '',
      hours: entry.hours || '',
      minutes: entry.minutes || '',
      category: entry.category || 'level1',
      description: entry.description || '',
      materials: entry.materials || [],
      discount: entry.discount || 0,
      discountType: entry.discountType || 'percent',
      taxRate: entry.taxRate || 0
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        hours: parseFloat(formData.hours) || 0,
        minutes: parseInt(formData.minutes) || 0,
        materials: formData.materials.filter(m => m.name && m.name.trim()),
        discount: parseFloat(formData.discount) || 0,
        discountType: formData.discountType || 'percent',
        taxRate: parseFloat(formData.taxRate) || 0
      };

      if (editingEntry) {
        await entriesApi.update(editingEntry.id, data);
      } else {
        await entriesApi.create(data);
      }

      setShowModal(false);
      setEditingEntry(null);
      setFormData({
        customerId: '',
        date: todayLocalISO(),
        hours: '',
        minutes: '',
        category: 'level1',
        description: '',
        materials: [],
        discount: 0,
        discountType: 'percent',
        taxRate: 0
      });
      setNewMaterial({ name: '', quantity: 1, unitPrice: 0, salesPrice: 0 });
      loadEntries();
    } catch (error) {
      console.error('Failed to save entry:', error);
      alert('Failed to save entry');
    }
  };

  // ── Custom Item actions ───────────────────────────────────────────────────
  const updateCustomItemStatus = async (id, status) => {
    try {
      await customItems.update(id, { status });
      // Update local state directly instead of reloading from server
      setCustomItemsList(prev => prev.map(ci => ci.id === id ? { ...ci, status } : ci));
    } catch (error) {
      console.error('Failed to update custom item status:', error);
      alert('Failed to update status');
    }
  };

  const deleteCustomItem = async (id) => {
    if (!window.confirm('Are you sure you want to DELETE this custom item?')) return;
    if (!window.confirm('PLEASE CONFIRM: This will permanently delete the item. This action cannot be undone.')) return;
    try {
      await customItems.delete(id);
      loadCustomItems();
    } catch (error) {
      console.error('Failed to delete custom item:', error);
      alert('Failed to delete custom item');
    }
  };

  const editCustomItem = (item) => {
    setEditingCustomItem(item);
    setCustomItemForm({
      customerId: item.customerId || '',
      date: item.date || '',
      name: item.name || '',
      description: item.description || '',
      quantity: item.quantity || 1,
      unitPrice: item.unitPrice || ''
    });
    setShowCustomItemModal(true);
  };

  const handleCustomItemSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...customItemForm,
        quantity: parseFloat(customItemForm.quantity) || 1,
        unitPrice: parseFloat(customItemForm.unitPrice) || 0
      };

      if (editingCustomItem) {
        await customItems.update(editingCustomItem.id, data);
      } else {
        await customItems.create(data);
      }

      setShowCustomItemModal(false);
      setEditingCustomItem(null);
      setCustomItemForm({
        customerId: '',
        date: todayLocalISO(),
        name: '',
        description: '',
        quantity: 1,
        unitPrice: ''
      });
      loadCustomItems();
    } catch (error) {
      console.error('Failed to save custom item:', error);
      alert('Failed to save custom item');
    }
  };

  // ── Shared helpers ─────────────────────────────────────────────────────────
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (date) => fmtDate(date);

  const getStatusBadge = (status) => {
    const badges = { unbilled: 'badge-warning', invoiced: 'badge-info', paid: 'badge-success' };
    return badges[status] || 'badge-info';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      level1: 'Level 1 ($135/hr)',
      level2: 'Level 2 ($245/hr)',
      consultation: 'Consultation ($125/hr)',
      emergency: 'Emergency ($202.50/hr — 1.5x)',
      other: 'Other'
    };
    return labels[category] || category;
  };

  const calcCustomItemTotal = () => {
    const qty = parseFloat(customItemForm.quantity) || 1;
    const price = parseFloat(customItemForm.unitPrice) || 0;
    return formatCurrency(qty * price);
  };

  const resetCustomItemForm = () => {
    setCustomItemForm({ customerId: '', date: todayLocalISO(), name: '', description: '', quantity: 1, unitPrice: '' });
    setEditingCustomItem(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCustomItemChange = (e) => {
    const { name, value } = e.target;
    setCustomItemForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleCustomItemFilterChange = (e) => {
    const { name, value } = e.target;
    setCustomItemFilters(prev => ({ ...prev, [name]: value }));
  };

  const addMaterial = () => {
    if (!newMaterial.name || !newMaterial.name.trim()) return;
    setFormData(prev => ({
      ...prev,
      materials: [...prev.materials, { ...newMaterial }]
    }));
    setNewMaterial({ name: '', quantity: 1, unitPrice: 0, salesPrice: 0 });
  };

  const removeMaterial = (index) => {
    setFormData(prev => ({
      ...prev,
      materials: prev.materials.filter((_, i) => i !== index)
    }));
  };

  const calcFormTotal = () => {
    const hours = parseFloat(formData.hours) || 0;
    const minutes = parseInt(formData.minutes) || 0;
    const totalHours = hours + minutes / 60;
    const rates = { level1: 135, level2: 245, consultation: 200, emergency: 202.5 };
    let rate = rates[formData.category] || 135;
    const materialsTotal = (formData.materials || []).reduce((s, m) => s + (m.quantity * (m.salesPrice || 0)), 0);
    const subtotal = totalHours * rate + materialsTotal;
    const discount = parseFloat(formData.discount) || 0;
    const discountAmount = formData.discountType === 'percent' ? subtotal * (discount / 100) : discount;
    const afterDiscount = subtotal - discountAmount;
    const tax = afterDiscount * ((parseFloat(formData.taxRate) || 0) / 100);
    return formatCurrency(afterDiscount + tax);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="page-header">
        <h1>Entries</h1>
        <button className="btn btn-primary" onClick={() => activeTab === 'time' ? setShowModal(true) : setShowCustomItemModal(true)}>
          + {activeTab === 'time' ? 'Add Time Entry' : 'Add Custom Item'}
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button
          className={`btn ${activeTab === 'time' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('time')}
        >
          Time Entries
        </button>
        <button
          className={`btn ${activeTab === 'custom' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('custom')}
        >
          Custom Items
        </button>
      </div>

      {/* ── TIME ENTRIES TAB ─────────────────────────────────────────────── */}
      {activeTab === 'time' && (
        <>
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Customer</label>
                <select name="customerId" className="form-select" value={filters.customerId} onChange={handleFilterChange}>
                  <option value="">All Customers</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select name="status" className="form-select" value={filters.status} onChange={handleFilterChange}>
                  <option value="">All</option>
                  <option value="unbilled">Unbilled</option>
                  <option value="invoiced">Invoiced</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input type="date" name="startDate" className="form-input" value={filters.startDate} onChange={handleFilterChange} />
              </div>
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input type="date" name="endDate" className="form-input" value={filters.endDate} onChange={handleFilterChange} />
              </div>
            </div>
          </div>

          <div className="card">
            {loading ? (
              <p>Loading...</p>
            ) : isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {entries.length === 0 ? (
                  <p style={{ textAlign: 'center', padding: 20 }}>No entries found</p>
                ) : entries.map(entry => (
                  <div key={entry.id} style={{ border: '1px solid #374151', borderRadius: 8, padding: 12, background: '#1f2937' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontWeight: 'bold', color: '#60a5fa' }}>{entry.customerName}</span>
                      <span style={{ fontWeight: 'bold', color: '#34d399' }}>{formatCurrency(entry.totalCost)}</span>
                    </div>
                    <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 4 }}>
                      {formatDate(entry.date)} • {getCategoryLabel(entry.category)} • {entry.hours}h {entry.minutes}m
                    </div>
                    <div style={{ fontSize: 13, color: '#d1d5db', marginBottom: 8 }}>
                      {entry.description || 'No description'}
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                        <input type="checkbox"
                          checked={entry.status === 'invoiced' || entry.status === 'paid'}
                          onChange={(e) => updateStatus(entry.id, e.target.checked ? 'invoiced' : 'unbilled')}
                          style={{ margin: 0, width: 18, height: 18 }} />
                        Invoiced
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                        <input type="checkbox"
                          checked={entry.status === 'paid'}
                          onChange={(e) => updateStatus(entry.id, e.target.checked ? 'paid' : 'invoiced')}
                          style={{ margin: 0, width: 18, height: 18 }} />
                        Paid
                      </label>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-danger btn-sm" onClick={() => deleteEntry(entry.id)}>Delete</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => editEntry(entry)}>Edit</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th><th>Customer</th><th>Category</th><th>Hours</th>
                      <th>Technician</th><th>Description</th><th>Materials</th>
                      <th>Amount</th><th>Status</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.length === 0 ? (
                      <tr><td colSpan="9" style={{ textAlign: 'center' }}>No entries found</td></tr>
                    ) : entries.map(entry => (
                      <tr key={entry.id}>
                        <td>{formatDate(entry.date)}</td>
                        <td>{entry.customerName}</td>
                        <td>{getCategoryLabel(entry.category)}</td>
                        <td>{entry.hours}h {entry.minutes}m</td>
                        <td>{entry.userName || entry.userUsername || '-'}</td>
                        <td>{entry.description || '-'}</td>
                        <td>{entry.materials?.length || 0} items</td>
                        <td>{formatCurrency(entry.totalCost)}</td>
                        <td>
                          <div style={{ display: 'inline-flex', gap: '12px', flexWrap: 'wrap' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                              <input type="checkbox" checked={entry.status === 'invoiced' || entry.status === 'paid'}
                                onChange={(e) => updateStatus(entry.id, e.target.checked ? 'invoiced' : 'unbilled')} style={{ margin: 0 }} />
                              <span>Invoiced</span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                              <input type="checkbox" checked={entry.status === 'paid'}
                                onChange={(e) => updateStatus(entry.id, e.target.checked ? 'paid' : 'invoiced')} style={{ margin: 0 }} />
                              <span>Paid</span>
                            </label>
                          </div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn btn-danger btn-sm" style={{ marginRight: 8 }} onClick={() => deleteEntry(entry.id)}>Delete</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => editEntry(entry)}>Edit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── CUSTOM ITEMS TAB ─────────────────────────────────────────────── */}
      {activeTab === 'custom' && (
        <>
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Customer</label>
                <select name="customerId" className="form-select" value={customItemFilters.customerId} onChange={handleCustomItemFilterChange}>
                  <option value="">All Customers</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select name="status" className="form-select" value={customItemFilters.status} onChange={handleCustomItemFilterChange}>
                  <option value="">All</option>
                  <option value="unbilled">Unbilled</option>
                  <option value="invoiced">Invoiced</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input type="date" name="startDate" className="form-input" value={customItemFilters.startDate} onChange={handleCustomItemFilterChange} />
              </div>
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input type="date" name="endDate" className="form-input" value={customItemFilters.endDate} onChange={handleCustomItemFilterChange} />
              </div>
            </div>
          </div>

          <div className="card">
            {customItemsLoading ? (
              <p>Loading...</p>
            ) : isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {customItemsList.length === 0 ? (
                  <p style={{ textAlign: 'center', padding: 20 }}>No custom items found</p>
                ) : customItemsList.map(item => (
                  <div key={item.id} style={{ border: '1px solid #374151', borderRadius: 8, padding: 12, background: '#1f2937' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontWeight: 'bold', color: '#60a5fa' }}>{item.customerName || '—'}</span>
                      <span style={{ fontWeight: 'bold', color: '#34d399' }}>{formatCurrency(item.totalPrice)}</span>
                    </div>
                    <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 4 }}>
                      {item.name} • {formatDate(item.date)} • {item.quantity} × {formatCurrency(item.unitPrice)}
                    </div>
                    {item.description && <div style={{ fontSize: 13, color: '#d1d5db', marginBottom: 8 }}>{item.description}</div>}
                    <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                        <input type="checkbox" checked={item.status === 'invoiced' || item.status === 'paid'}
                          onChange={(e) => updateCustomItemStatus(item.id, e.target.checked ? 'invoiced' : 'unbilled')}
                          style={{ margin: 0, width: 18, height: 18 }} />
                        Invoiced
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                        <input type="checkbox" checked={item.status === 'paid'}
                          onChange={(e) => updateCustomItemStatus(item.id, e.target.checked ? 'paid' : 'invoiced')}
                          style={{ margin: 0, width: 18, height: 18 }} />
                        Paid
                      </label>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-danger btn-sm" onClick={() => deleteCustomItem(item.id)}>Delete</button>
                      <button className="btn btn-secondary btn-sm" onClick={() => editCustomItem(item)}>Edit</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th><th>Customer</th><th>Name</th><th>Description</th>
                      <th>Qty</th><th>Unit Price</th><th>Total</th>
                      <th>Status</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customItemsList.length === 0 ? (
                      <tr><td colSpan="8" style={{ textAlign: 'center' }}>No custom items found</td></tr>
                    ) : customItemsList.map(item => (
                      <tr key={item.id}>
                        <td>{formatDate(item.date)}</td>
                        <td>{item.customerName || '—'}</td>
                        <td>{item.name}</td>
                        <td>{item.description || '—'}</td>
                        <td>{item.quantity}</td>
                        <td>{formatCurrency(item.unitPrice)}</td>
                        <td>{formatCurrency(item.totalPrice)}</td>
                        <td>
                          <div style={{ display: 'inline-flex', gap: '12px', flexWrap: 'wrap' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                              <input type="checkbox" checked={item.status === 'invoiced' || item.status === 'paid'}
                                onChange={(e) => updateCustomItemStatus(item.id, e.target.checked ? 'invoiced' : 'unbilled')} style={{ margin: 0 }} />
                              <span>Invoiced</span>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                              <input type="checkbox" checked={item.status === 'paid'}
                                onChange={(e) => updateCustomItemStatus(item.id, e.target.checked ? 'paid' : 'invoiced')} style={{ margin: 0 }} />
                              <span>Paid</span>
                            </label>
                          </div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn btn-danger btn-sm" style={{ marginRight: 8 }} onClick={() => deleteCustomItem(item.id)}>Delete</button>
                          <button className="btn btn-secondary btn-sm" onClick={() => editCustomItem(item)}>Edit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── TIME ENTRY MODAL ──────────────────────────────────────────────── */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 600 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingEntry ? 'Edit Time Entry' : 'Add Time Entry'}</h2>
              <button className="modal-close" onClick={() => { setShowModal(false); setEditingEntry(null); setFormData({ customerId: '', date: todayLocalISO(), hours: '', minutes: '', category: 'level1', description: '', materials: [], discount: 0, discountType: 'percent', taxRate: 0 }); }}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Customer *</label>
                  <select name="customerId" className="form-select" value={formData.customerId} onChange={handleChange} required>
                    <option value="">Select a customer</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input type="date" name="date" className="form-input" value={formData.date} onChange={handleChange} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Hours</label>
                    <input type="number" name="hours" className="form-input" value={formData.hours} onChange={handleChange} min="0" step="0.25" placeholder="0" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Minutes</label>
                    <input type="number" name="minutes" className="form-input" value={formData.minutes} onChange={handleChange} min="0" max="59" placeholder="0" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Quick Service</label>
                    <select className="form-select" value={selectedService} onChange={(e) => {
                      const service = recurringServices.find(s => s.id === e.target.value);
                      if (service) {
                        setFormData({ ...formData, hours: service.hours.toString(), description: service.description });
                      }
                      setSelectedService(e.target.value);
                    }}>
                      <option value="">-- Select Service --</option>
                      {recurringServices.map(s => <option key={s.id} value={s.id}>{s.name} ({s.hours}h)</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Fee Type</label>
                    <select name="category" className="form-select" value={formData.category} onChange={handleChange}>
                      <option value="level1">Level 1 ($135/hr)</option>
                      <option value="level2">Level 2 ($245/hr)</option>
                      <option value="consultation">Consultation ($125/hr)</option>
                      <option value="emergency">Emergency ($202.50/hr — 1.5x)</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea name="description" className="form-textarea" value={formData.description} onChange={handleChange} placeholder="What did you work on?" />
                </div>

                {/* Running total preview */}
                <div style={{ background: '#1f2937', borderRadius: 6, padding: '8px 12px', marginBottom: 16, fontSize: 13, color: '#9ca3af' }}>
                  Estimated Total: <strong style={{ color: '#34d399' }}>{calcFormTotal()}</strong>
                </div>

                {/* Discount Section */}
                <div style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 20 }}>
                  <h4 style={{ marginBottom: 12 }}>Discount</h4>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Discount</label>
                      <input type="number" name="discount" className="form-input" value={formData.discount} onChange={handleChange} placeholder="0" min="0" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Type</label>
                      <select name="discountType" className="form-select" value={formData.discountType} onChange={handleChange}>
                        <option value="percent">%</option>
                        <option value="fixed">$ Fixed</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group" style={{ marginTop: 10 }}>
                    <label className="form-label">Tax Rate (%)</label>
                    <input type="number" name="taxRate" className="form-input" value={formData.taxRate} onChange={handleChange} placeholder="0" min="0" max="100" step="0.01" />
                  </div>
                </div>

                {/* Materials Section */}
                <div style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 20 }}>
                  <h4 style={{ marginBottom: 12 }}>Materials</h4>
                  <div className="form-row">
                    <div className="form-group" style={{ flex: 2 }}>
                      <label className="form-label">Item Name</label>
                      <input type="text" className="form-input" value={newMaterial.name}
                        onChange={(e) => setNewMaterial({ ...newMaterial, name: e.target.value })} placeholder="e.g., HDMI Cable" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Qty</label>
                      <input type="number" className="form-input" value={newMaterial.quantity}
                        onChange={(e) => setNewMaterial({ ...newMaterial, quantity: parseInt(e.target.value) || 1 })} min="1" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Cost</label>
                      <input type="number" className="form-input" value={newMaterial.unitPrice}
                        onChange={(e) => setNewMaterial({ ...newMaterial, unitPrice: parseFloat(e.target.value) || 0 })} step="0.01" min="0" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Sales</label>
                      <input type="number" className="form-input" value={newMaterial.salesPrice}
                        onChange={(e) => setNewMaterial({ ...newMaterial, salesPrice: parseFloat(e.target.value) || 0 })} step="0.01" min="0" />
                    </div>
                  </div>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={addMaterial}>+ Add Material</button>

                  {formData.materials.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <table className="table">
                        <thead><tr><th>Item</th><th>Qty</th><th>Cost</th><th>Sales</th><th></th></tr></thead>
                        <tbody>
                          {formData.materials.map((m, idx) => (
                            <tr key={idx}>
                              <td>{m.name}</td><td>{m.quantity}</td><td>{formatCurrency(m.unitPrice)}</td><td>{formatCurrency(m.salesPrice)}</td>
                              <td><button type="button" className="btn btn-danger btn-sm" onClick={() => removeMaterial(idx)}>X</button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Existing custom items section */}
                <div style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 20 }}>
                  <h4 style={{ marginBottom: 12 }}>Add from Custom Items</h4>
                  {customItemsList.filter(ci => ci.status !== 'paid').length === 0 ? (
                    <p style={{ fontSize: 13, color: '#6b7280' }}>No unbilled custom items</p>
                  ) : (
                    <div style={{ maxHeight: 120, overflowY: 'auto', border: '1px solid #374151', borderRadius: 6 }}>
                      {customItemsList.filter(ci => ci.status !== 'paid').map(ci => (
                        <div key={ci.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', borderBottom: '1px solid #374151' }}>
                          <div>
                            <div style={{ fontSize: 13 }}>{ci.name}</div>
                            <div style={{ fontSize: 11, color: '#6b7280' }}>{ci.quantity} × {formatCurrency(ci.unitPrice)} = {formatCurrency(ci.totalPrice)}</div>
                          </div>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              materials: [...prev.materials, { name: ci.name, quantity: ci.quantity, unitPrice: ci.unitPrice, salesPrice: ci.unitPrice }]
                            }));
                          }}>Add</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editingEntry ? 'Update Entry' : 'Create Entry'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── CUSTOM ITEM MODAL ──────────────────────────────────────────────── */}
      {showCustomItemModal && (
        <div className="modal-overlay" onClick={() => { setShowCustomItemModal(false); resetCustomItemForm(); }}>
          <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingCustomItem ? 'Edit Custom Item' : 'Add Custom Item'}</h2>
              <button className="modal-close" onClick={() => { setShowCustomItemModal(false); resetCustomItemForm(); }}>&times;</button>
            </div>
            <form onSubmit={handleCustomItemSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Customer</label>
                  <select name="customerId" className="form-select" value={customItemForm.customerId} onChange={handleCustomItemChange}>
                    <option value="">No customer (general)</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Date</label>
                  <input type="date" name="date" className="form-input" value={customItemForm.date} onChange={handleCustomItemChange} />
                </div>
                <div className="form-group">
                  <label className="form-label">Item Name *</label>
                  <input type="text" name="name" className="form-input" value={customItemForm.name}
                    onChange={handleCustomItemChange} placeholder="e.g., Trip Charge, Setup Fee" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea name="description" className="form-textarea" value={customItemForm.description}
                    onChange={handleCustomItemChange} placeholder="Optional description" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Quantity</label>
                    <input type="number" name="quantity" className="form-input" value={customItemForm.quantity}
                      onChange={handleCustomItemChange} min="0" step="0.01" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Unit Price ($) *</label>
                    <input type="number" name="unitPrice" className="form-input" value={customItemForm.unitPrice}
                      onChange={handleCustomItemChange} min="0" step="0.01" placeholder="0.00" required />
                  </div>
                </div>
                {/* Total preview */}
                <div style={{ background: '#1f2937', borderRadius: 6, padding: '8px 12px', marginTop: 8, fontSize: 13, color: '#9ca3af' }}>
                  Total: <strong style={{ color: '#34d399' }}>{calcCustomItemTotal()}</strong>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowCustomItemModal(false); resetCustomItemForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingCustomItem ? 'Update Item' : 'Create Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Entries;
